'use client';

import { useMobile } from 'common-nextjs';
import { Form, Formik } from 'formik';
import { FormikField } from 'formik-ui';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import { Button, Label } from 'ui';
import * as yup from 'yup';
import { postEmailCapture } from '~/api/user';
import { setKnownCookie } from '~/hooks/cookies';
import { fireEmailCaptured } from '~/services/analytics/events/item';

interface EmailCaptureFormValues {
  email: string;
  zip: string;
}

const schema: yup.SchemaOf<EmailCaptureFormValues> = yup.object({
  email: yup.string().required('Required').email('Enter a valid email'),
  zip: yup.string().required('Required').min(5, 'Enter a valid zip code'),
});

interface Props {}

const TradeInEventsEmailCapture: React.FC<Props> = () => {
  const pathname = usePathname();
  const mobile = useMobile();
  const [justCaptured, setJustCaptured] = useState(false);

  const email = (
    <FormikField
      label="Email"
      name="email"
      type="email"
      className="w-full md:w-80"
      placeholder="Enter your email"
    />
  );

  const zip = (
    <FormikField label="Zip" name="zip" placeholder="01234" className="w-32" />
  );

  const submitButton = (
    <div className="justify-self-end">
      <Label className="block h-6" />
      <Button className="h-10" type="submit">
        Subscribe
      </Button>
    </div>
  );

  return (
    <div className="mb-6 flex flex-col">
      {justCaptured ? (
        <div className="w-full text-lg font-semibold">
          Thanks, we&apos;ll let you know when an event is coming to your area!
        </div>
      ) : (
        <div>
          <div className="mb-4 text-base font-semibold md:text-xl">
            Get alerts when events are added in your area
          </div>

          <Formik<EmailCaptureFormValues>
            initialValues={{ email: '', zip: '' }}
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={async values => {
              try {
                await postEmailCapture(
                  values.email,
                  'TRADE_IN_EVENTS',
                  values.zip,
                );
              } catch (e) {
                fireEmailCaptured({
                  email: values.email,
                  pageName: pathname!,
                  zip: values.zip,
                });
              }
              setJustCaptured(true);
              setKnownCookie(undefined, 'sls_ti_email_captured', '1');
              setKnownCookie(undefined, 'sls_ec', '1');
            }}
          >
            <Form>
              {mobile ? (
                <div className="grid grid-cols-2 gap-x-4">
                  <div className="col-span-2">{email}</div>
                  {zip}
                  {submitButton}
                </div>
              ) : (
                <div className="flex space-x-4">
                  {email}
                  {zip}
                  {submitButton}
                </div>
              )}
            </Form>
          </Formik>
        </div>
      )}
    </div>
  );
};

export default TradeInEventsEmailCapture;
